import { Page } from "../layout/page";
import { Title, Box, Text, Avatar, Group, Badge, ScrollArea } from "@mantine/core";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/useServerApi";
import { useFormatter } from "../hooks/useFomatter";
import { useUserFriendlyDate } from "../hooks/useUserFriendlyDate";
import _ from "lodash";
import {
  IconUser,
  IconFileText,
  IconBox,
  IconCalendarStats,
  IconCoinMonero,
  IconSubtask,
  IconFileInvoice,
  IconUsers,
} from "@tabler/icons-react";

const Notification = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [userFriendlyDate] = useUserFriendlyDate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAllLogs = async () => {
    try {
      setLoading(true);
      const result = await api.getAllActivityLogs();
      if (result && result.docs) {
        setLogs(result.docs);
      }
    } catch (error) {
      console.error("Error fetching activity logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllLogs();
  }, []);

  const getByName = (by) => (by ? formatter.friendlyName(by) : "SYSTEM");

  const getEntityTypeFromId = (entityId) => {
    // This is a simplified mapping - in production you might want to
    // store entityType in the activity log itself
    return "Entity";
  };

  const getEntityIcon = (title) => {
    if (title?.toLowerCase().includes("project")) return <IconCalendarStats size={16} />;
    if (title?.toLowerCase().includes("client")) return <IconUsers size={16} />;
    if (title?.toLowerCase().includes("item")) return <IconBox size={16} />;
    if (title?.toLowerCase().includes("claim")) return <IconCoinMonero size={16} />;
    if (title?.toLowerCase().includes("task")) return <IconSubtask size={16} />;
    if (title?.toLowerCase().includes("invoice")) return <IconFileInvoice size={16} />;
    if (title?.toLowerCase().includes("quotation")) return <IconFileText size={16} />;
    return <IconFileText size={16} />;
  };

  const getLogTypeBadge = (type) => {
    const color = type === "COMMENT" ? "blue" : "green";
    return (
      <Badge color={color} size="sm" variant="light">
        {type}
      </Badge>
    );
  };

  return (
    <Page title={t("Notifications")}>
      <Box mt="lg">
        <ScrollArea style={{ height: "calc(100vh - 200px)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "var(--mantine-color-gray-0)",
                zIndex: 1,
              }}
            >
              <tr>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {t("Who")}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {t("Entity Type")}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {t("Entity Code")}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {t("Log Content")}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {t("Type")}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {t("Date")}
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr
                  key={log._id || index}
                  style={{
                    backgroundColor:
                      index % 2 === 0
                        ? "var(--mantine-color-white)"
                        : "var(--mantine-color-gray-0)",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      "var(--mantine-color-blue-0)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      index % 2 === 0
                        ? "var(--mantine-color-white)"
                        : "var(--mantine-color-gray-0)")
                  }
                >
                  <td
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--mantine-color-gray-2)",
                    }}
                  >
                    <Group spacing="sm">
                      <Avatar
                        src={
                          log.by?.image
                            ? `${process.env.REACT_APP_FILE_URL}/${log.by.image}`
                            : null
                        }
                        size="sm"
                        radius="xl"
                      >
                        <IconUser size={16} />
                      </Avatar>
                      <Text size="sm">{getByName(log.by)}</Text>
                    </Group>
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--mantine-color-gray-2)",
                    }}
                  >
                    <Group spacing="xs">
                      {getEntityIcon(log.title)}
                      <Text size="sm" color="dimmed">
                        {getEntityTypeFromId(log.entityId)}
                      </Text>
                    </Group>
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--mantine-color-gray-2)",
                    }}
                  >
                    <Text size="sm" ff="monospace">
                      {log.entityId
                        ? log.entityId.toString().slice(-8).toUpperCase()
                        : "N/A"}
                    </Text>
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--mantine-color-gray-2)",
                      maxWidth: "300px",
                    }}
                  >
                    <Text size="sm" lineClamp={2}>
                      {log.title || log.description || "-"}
                    </Text>
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--mantine-color-gray-2)",
                    }}
                  >
                    {getLogTypeBadge(log.type)}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--mantine-color-gray-2)",
                    }}
                  >
                    <Text size="sm" color="dimmed">
                      {userFriendlyDate(log.createdAt, "LLL")}
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && !loading && (
            <Box p="xl" ta="center">
              <Text color="dimmed">{t("No activity logs found")}</Text>
            </Box>
          )}
        </ScrollArea>
      </Box>
    </Page>
  );
};

export default Notification;
